import { useEffect, useState, type FormEvent } from "react";
import type { HandoverInfo, ProjectDetail } from "@handvero/shared";
import { api, errorText } from "../api";
import { Message, Sheet } from "./ui";

/* Übergabe-Akte: Unterlagen eines Projekts (gewählte Ordner) per Link an den Kunden übergeben. Er sieht sie ohne Konto. */

const day = (iso: string) => new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

export function HandoverSheet({ project, onClose }: { project: ProjectDetail; onClose: () => void }) {
  const top = project.folders.filter((f) => f.parentId === null);
  const [picked, setPicked] = useState<string[]>(top.map((f) => f.id));
  const [name, setName] = useState(project.customer ?? "");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [days, setDays] = useState(90);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [list, setList] = useState<HandoverInfo[]>([]);

  const load = () => api.get<HandoverInfo[]>(`/api/projects/${project.id}/handovers`).then(setList, () => undefined);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  async function send(e: FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post(`/api/projects/${project.id}/handovers`, { name, email, message: message || undefined, folderIds: picked, days });
      setSent(true);
      setEmail("");
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    await api.del(`/api/handovers/${id}`).catch((err) => setError(errorText(err)));
    await load();
  }

  const active = list.filter((h) => !h.revoked && new Date(h.expiresAt).getTime() > Date.now());
  return (
    <Sheet title="Übergabe-Akte an den Kunden" onClose={onClose}>
      <form className="device" onSubmit={send}>
        <p>
          Der Kunde bekommt einen Link und sieht die gewählten Ordner mit Unterordnern, ohne Konto. Mit einem kostenlosen Privatkonto kann er sie mit einem
          Klick in seine Hausakte übernehmen.
        </p>
        <fieldset style={{ border: 0, padding: 0, margin: 0, display: "grid", gap: 6 }}>
          <legend>
            <b>Welche Ordner?</b>
          </legend>
          {top.map((f) => (
            <label key={f.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={picked.includes(f.id)}
                onChange={(e) => setPicked((p) => (e.target.checked ? [...p, f.id] : p.filter((x) => x !== f.id)))}
              />
              <span>{f.name.replace(/^\d+_/, "")}</span>
            </label>
          ))}
        </fieldset>
        <label className="field">
          <span>Name des Kunden</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required />
        </label>
        <label className="field">
          <span>E-Mail des Kunden</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" required />
        </label>
        <label className="field">
          <span>Nachricht (freiwillig)</span>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={1000} rows={3} />
        </label>
        <label className="field">
          <span>Link gültig für</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={30}>30 Tage</option>
            <option value={90}>90 Tage</option>
            <option value={180}>180 Tage</option>
          </select>
        </label>
        <Message>{error}</Message>
        {sent && <p>Die E-Mail ist unterwegs.</p>}
        <button className="dbtn" type="submit" disabled={busy || picked.length === 0}>
          {busy ? "Wird verschickt …" : "Übergabe schicken"}
        </button>
      </form>
      {active.length > 0 && (
        <div className="device" style={{ marginTop: 16 }}>
          <b>Bereits übergeben</b>
          <ul className="acc-devices">
            {active.map((h) => (
              <li key={h.id}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <b>{h.name}</b>
                  <br />
                  <small>
                    bis {day(h.expiresAt)}
                    {h.claimedAt ? " · übernommen" : h.lastOpenedAt ? " · geöffnet" : " · noch nicht geöffnet"}
                  </small>
                </span>
                <button type="button" className="dbtn ghost" onClick={() => void revoke(h.id)}>
                  Zurückziehen
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Sheet>
  );
}
